/**
 * Scanyt — serveur local.
 *
 * Ce fichier contient tout ce qui n'est pas la connexion IMAP (voir scanner.js) :
 *   - les préréglages IMAP des fournisseurs courants,
 *   - la sauvegarde locale des résultats (data/accounts.json),
 *   - la catégorisation + les liens de suppression (services.json),
 *   - les routes de l'API utilisées par l'interface web (dossier public/).
 *
 * Le serveur n'écoute que sur 127.0.0.1 : personne d'autre sur le réseau
 * (wifi public, etc.) ne peut atteindre l'outil ni tes données.
 */
const fs = require('fs');
const path = require('path');
const os = require('os');
const crypto = require('crypto');
const express = require('express');
const {
  scanMailbox, baseDomain, serviceNameFromDomain, DEFAULT_MAX_MESSAGES
} = require('./scanner');

const PORT = process.env.PORT || 4173;

/**
 * Où sont écrites TES données (résultats, statuts, notes, logos en cache).
 *
 * Lancé depuis le dossier du projet — un dossier téléchargé, un clone, un
 * `npm start` — elles restent dans data/, juste à côté du code : c'est visible,
 * et rien ne change pour qui utilise Scanyt comme avant.
 *
 * Installé depuis npm (`npx scanyt`), le dossier du programme vit dans un cache
 * partagé, parfois en lecture seule, effacé sans prévenir : les données vont
 * alors dans le dossier personnel, à l'emplacement prévu par le système.
 *
 * SCANYT_DATA_DIR permet de forcer l'emplacement (clé USB, dossier chiffré…).
 */
function resoudreDossierDonnees() {
  if (process.env.SCANYT_DATA_DIR) return path.resolve(process.env.SCANYT_DATA_DIR);

  const installeDepuisNpm = /[\\/](?:node_modules|_npx)[\\/]/.test(__dirname);
  if (!installeDepuisNpm) return path.join(__dirname, 'data');

  const maison = os.homedir();
  if (process.platform === 'darwin') {
    return path.join(maison, 'Library', 'Application Support', 'Scanyt');
  }
  if (process.platform === 'win32') {
    return path.join(process.env.APPDATA || path.join(maison, 'AppData', 'Roaming'), 'Scanyt');
  }
  return path.join(process.env.XDG_DATA_HOME || path.join(maison, '.local', 'share'), 'scanyt');
}

const DATA_DIR = resoudreDossierDonnees();
const DATA_FILE = path.join(DATA_DIR, 'accounts.json');
const IGNORED_FILE = path.join(DATA_DIR, 'ignored.json');
const CATEGORY_MAP_FILE = path.join(DATA_DIR, 'categories.json');
const GROUPS_FILE = path.join(DATA_DIR, 'groups.json');
const LOCAL_KNOWLEDGE_FILE = path.join(DATA_DIR, 'knowledge.local.json');

// ---------------------------------------------------------------------------
// Préréglages IMAP. « custom » laisse l'utilisateur saisir son propre serveur.
// ---------------------------------------------------------------------------
const PROVIDERS = {
  gmail: { label: 'Gmail / Google Workspace', host: 'imap.gmail.com', port: 993, secure: true },
  outlook: { label: 'Outlook / Hotmail / Live', host: 'outlook.office365.com', port: 993, secure: true },
  yahoo: { label: 'Yahoo Mail', host: 'imap.mail.yahoo.com', port: 993, secure: true },
  icloud: { label: 'iCloud Mail', host: 'imap.mail.me.com', port: 993, secure: true },
  orange: { label: 'Orange', host: 'imap.orange.fr', port: 993, secure: true },
  free: { label: 'Free (free.fr)', host: 'imap.free.fr', port: 993, secure: true },
  laposte: { label: 'La Poste', host: 'imap.laposte.net', port: 993, secure: true },
  custom: { label: 'Autre / serveur personnalisé', host: '', port: 993, secure: true }
};

// ---------------------------------------------------------------------------
// Base de connaissances : catégorie et lien de suppression par domaine.
// ---------------------------------------------------------------------------
const knowledge = require('./services.json');
const DEFAULT_CATEGORY = knowledge.defaultCategory || 'Autre';
const CATEGORY_DOMAINS = knowledge.categories.domains || {};
const CATEGORY_RULES = knowledge.categories.rules || [];
const MOTS_DOMAINE = knowledge.categories.motsDomaine || {};
const MOTS_OBJET = knowledge.categories.motsObjet || {};
const DELETION_LINKS = knowledge.deletionLinks || {};
const LOGO_ALIASES = knowledge.logoAliases || {};
const BRAND_ALIASES = knowledge.brandAliases || {};
const BRAND_NAMES = knowledge.brandNames || {};

// Fuites de données connues (Have I Been Pwned), importées une fois par
// tools/import-fuites.js. Absentes, l'outil marche comme avant.
const FUITES = (() => {
  try { return require('./fuites.json').domaines || {}; } catch (err) { return {}; }
})();

/** La fuite connue pour ce domaine (ou son domaine parent), sinon null. */
function fuiteFor(domain) {
  const parties = String(domain || '').toLowerCase().split('.');
  for (let i = 0; i < parties.length - 1; i++) {
    const candidat = parties.slice(i).join('.');
    if (FUITES[candidat]) return { domaine: candidat, ...FUITES[candidat] };
  }
  return null;
}

// Base publique JustDeleteMe, importée une fois par tools/import-deletion-links.js.
// Elle complète deletionLinks de services.json : 5 500 domaines contre 69, avec
// en prime la difficulté de la démarche. Absente, l'outil marche comme avant.
const JDM = (() => {
  try {
    const brut = require('./deletion-links.json');
    return { fiches: brut.fiches || [], domaines: brut.domaines || {} };
  } catch (err) {
    return { fiches: [], domaines: {} };
  }
})();

// ---------------------------------------------------------------------------
// Regroupement par marque.
//
// Un même service écrit souvent depuis plusieurs domaines : proton.me et
// notify.proton.me, uniqlo.eu et ml.store.uniqlo.com, dazn.com et
// service-mail.dazn.com. Ils apparaissaient comme autant de services distincts,
// parfois sous des noms absurdes (« Ml », « Notify »). On les réunit en une
// seule ligne, dont le dépliage montre les emails de toutes les variantes.
// ---------------------------------------------------------------------------

// Extensions à deux niveaux : sans elles, impots.gouv.fr et def.gouv.fr
// seraient regroupés sous « gouv », ce qui n'aurait aucun sens.
const MULTI_LABEL_SUFFIXES = new Set([
  'co.uk', 'org.uk', 'gov.uk', 'ac.uk', 'me.uk', 'net.uk',
  'com.au', 'net.au', 'org.au', 'co.nz', 'co.jp', 'co.kr', 'co.in', 'co.za',
  'gouv.fr', 'asso.fr', 'com.fr', 'tm.fr',
  'com.br', 'com.mx', 'com.tr', 'com.pl', 'com.es', 'com.pt', 'com.it'
]);

// ---------------------------------------------------------------------------
// Regroupements créés à la main.
//
// La détection automatique ne peut pas tout deviner : deux domaines d'une même
// enseigne peuvent n'avoir aucun mot en commun. L'utilisateur peut donc
// sélectionner plusieurs services et les réunir sous un nom de son choix.
// ---------------------------------------------------------------------------
function readGroups() {
  try {
    return JSON.parse(fs.readFileSync(GROUPS_FILE, 'utf8'));
  } catch (err) {
    if (err.code === 'ENOENT') return {};
    throw err;
  }
}

function writeGroups(data) {
  fs.mkdirSync(path.dirname(GROUPS_FILE), { recursive: true });
  const tmp = GROUPS_FILE + '.tmp';
  fs.writeFileSync(tmp, JSON.stringify(data, null, 2), 'utf8');
  fs.renameSync(tmp, GROUPS_FILE);
}

function manualGroupsFor(email) {
  const all = readGroups();
  return Array.isArray(all[email]) ? all[email] : [];
}

/** Domaine -> nom du groupe manuel auquel il appartient. */
function manualGroupIndex(groups) {
  const index = new Map();
  for (const groupe of groups) {
    for (const domain of groupe.domains || []) index.set(domain.toLowerCase(), groupe.name);
  }
  return index;
}

/** Ajoute (ou remplace) un groupe dans une liste, sans toucher au disque. */
function ajouterGroupe(groupes, name, domains) {
  const propres = [...new Set(domains.map(d => String(d).toLowerCase()))];
  // Un domaine n'appartient qu'à un seul groupe : on le retire des autres.
  const restants = groupes
    .filter(g => g.name !== name)
    .map(g => ({ ...g, domains: (g.domains || []).filter(d => !propres.includes(d)) }))
    .filter(g => g.domains.length > 1);
  restants.push({ name: String(name), domains: propres });
  return restants;
}

function saveManualGroup(email, name, domains) {
  const all = readGroups();
  all[email] = ajouterGroupe(manualGroupsFor(email), name, domains);
  writeGroups(all);
  return all[email];
}

function deleteManualGroup(email, name) {
  const all = readGroups();
  all[email] = manualGroupsFor(email).filter(g => g.name !== name);
  writeGroups(all);
  return all[email];
}

/** Clé de regroupement : le nom de marque porté par le domaine. */
function brandKey(domain) {
  const d = String(domain || '').toLowerCase();
  const base = baseDomain(d);
  const parts = base.split('.');
  const registrable = parts.slice(-2).join('.');

  // Une marque peut se cacher derrière un domaine qui ne lui ressemble pas
  // (connect.sncf, samsung-mail.com…) : services.json fait le lien.
  for (const candidate of [d, base, registrable]) {
    if (BRAND_ALIASES[candidate]) return BRAND_ALIASES[candidate];
  }

  if (parts.length < 2) return parts[0] || d;
  const index = MULTI_LABEL_SUFFIXES.has(registrable) ? parts.length - 3 : parts.length - 2;
  return parts[index] || parts[0];
}

/** Domaine « racine » d'un service : acct.epicgames.com -> epicgames.com. */
function registrableDomain(domain) {
  const parts = baseDomain(domain).split('.');
  if (parts.length <= 2) return parts.join('.');
  const lastTwo = parts.slice(-2).join('.');
  return MULTI_LABEL_SUFFIXES.has(lastTwo) ? parts.slice(-3).join('.') : lastTwo;
}

/** Domaine représentatif d'un groupe : la racine du membre le plus court. */
function primaryDomain(domains) {
  const court = [...domains].sort((a, b) => {
    const da = a.split('.').length, db = b.split('.').length;
    return da !== db ? da - db : (a.length !== b.length ? a.length - b.length : a.localeCompare(b));
  })[0];
  return registrableDomain(court);
}

/**
 * Nom lisible d'un service, tiré de la marque et non du sous-domaine émetteur :
 * « Trainline » plutôt que « Comms », « Uniqlo » plutôt que « Ml ».
 */
function brandName(domain) {
  const key = brandKey(domain);
  // Le nom déduit du domaine est parfois maladroit (« Societegenerale »,
  // « Iledefrance-mobilites ») : services.json peut fournir le vrai libellé.
  return BRAND_NAMES[key] || serviceNameFromDomain(key);
}

/**
 * Réunit les entrées d'une même marque en une seule ligne. Les données
 * stockées restent par domaine : le regroupement est purement un affichage,
 * donc toujours réversible.
 */
function groupAccounts(accounts, manualGroups = []) {
  const manuel = manualGroupIndex(manualGroups);
  const groupes = new Map();
  const nomsManuels = new Map();
  for (const account of accounts) {
    // Un regroupement manuel prime toujours sur la détection automatique.
    const nom = manuel.get(account.domain.toLowerCase());
    const key = nom ? `manuel:${nom}` : brandKey(account.domain);
    if (nom) nomsManuels.set(key, nom);
    if (!groupes.has(key)) groupes.set(key, []);
    groupes.get(key).push(account);
  }

  return Array.from(groupes.entries()).map(([key, membres]) => {
    const nomManuel = nomsManuels.get(key) || null;
    if (membres.length === 1 && !nomManuel) {
      // Seul domaine : on garde l'adresse réellement émettrice, mais on
      // corrige le nom affiché.
      return { ...membres[0], serviceName: brandName(membres[0].domain), domains: [membres[0].domain] };
    }

    const domains = membres.map(m => m.domain).sort();
    const principal = primaryDomain(domains);
    const base = membres.find(m => m.domain === principal) || membres[0];
    const dates = k => membres.map(m => m[k]).filter(Boolean).sort();

    return {
      ...base,
      domain: principal,
      domains,
      manualGroup: nomManuel,
      newsletter: membres.some(m => m.newsletter),
      confirmed: membres.some(m => m.confirmed !== false),
      unsubscribedAt: (membres.find(m => m.unsubscribedAt) || {}).unsubscribedAt || null,
      rgpdRequestedAt: dates('rgpdRequestedAt')[0] || null,
      // La marque est nouvelle seulement si tous ses domaines le sont : un
      // nouveau domaine d'expédition chez un service connu n'est pas un compte.
      discoveredAt: membres.some(m => !m.discoveredAt) ? null : dates('discoveredAt').pop(),
      lastScanAt: dates('lastScanAt').pop() || null,
      ...meilleureDesinscription(membres),
      serviceName: nomManuel || brandName(principal),
      count: membres.reduce((total, m) => total + (Number(m.count) || 0), 0),
      firstSeen: dates('firstSeen')[0] || null,
      lastSeen: dates('lastSeen').pop() || null,
      messages: mergeMessages(membres.flatMap(m => m.messages || []), [])
    };
  });
}

/** Tous les domaines stockés qui appartiennent au même groupe que `domain`. */
function groupMembers(email, domain) {
  const stored = Object.keys(readAll()[email] || {});
  const manuel = manualGroupIndex(manualGroupsFor(email));
  const nom = manuel.get(String(domain).toLowerCase());
  if (nom) return stored.filter(d => manuel.get(d.toLowerCase()) === nom);
  const key = brandKey(domain);
  const membres = stored.filter(d => !manuel.has(d.toLowerCase()) && brandKey(d) === key);
  return membres.length ? membres : [domain];
}

// Un même service décline souvent son domaine par pays : paypal.com et
// paypal.fr, zalando.com et zalando.fr… La base ne peut pas tous les lister.
// On l'indexe donc par NOM DE MARQUE (le label devant l'extension), mais
// uniquement quand ce nom ne renvoie qu'à une seule catégorie : à la moindre
// ambiguïté, on préfère ne rien conclure.
const GENERIC_LABELS = new Set([
  'mail', 'news', 'shop', 'store', 'app', 'apps', 'web', 'club', 'group',
  'info', 'contact', 'service', 'services', 'box', 'max', 'my', 'pro', 'plus',
  'online', 'home', 'media', 'cloud', 'blog', 'forum', 'chat', 'jobs'
]);

const CATEGORY_BY_BRAND = (() => {
  const parCategorie = new Map();
  for (const [domain, category] of Object.entries(CATEGORY_DOMAINS)) {
    const label = domain.split('.')[0];
    if (GENERIC_LABELS.has(label)) continue;
    if (!parCategorie.has(label)) parCategorie.set(label, new Set());
    parCategorie.get(label).add(category);
  }
  const index = {};
  for (const [label, categories] of parCategorie) {
    if (categories.size === 1) index[label] = categories.values().next().value;
  }
  return index;
})();

/** Minuscules sans accents : « Réservation » et « reservation » se valent. */
function sansAccents(texte) {
  return String(texte || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Un mot du nom de domaine trahit souvent le secteur : betclic (bet),
 * hostelworld (hostel), jai20enmaths (maths). Les mots courts ne comptent
 * qu'en début ou fin d'étiquette (« bet » dans betclic, pas dans alphabet),
 * ceux de deux lettres seulement comme étiquette entière (« tv », « ai »).
 */
function categorieParMotsDuDomaine(domain) {
  const etiquettes = sansAccents(domain).split(/[.-]/).filter(Boolean);
  for (const [categorie, mots] of Object.entries(MOTS_DOMAINE)) {
    for (const mot of mots) {
      const trouve = etiquettes.some(e => mot.length <= 2 ? e === mot
        : mot.length <= 4 ? (e === mot || e.startsWith(mot) || e.endsWith(mot))
          : e.includes(mot));
      if (trouve) return categorie;
    }
  }
  return null;
}

/**
 * En dernier recours, les objets des emails : « Vos e-billets », « Suivi de
 * la commande », « bienvenue dans ta formation »… La catégorie la plus citée
 * l'emporte ; il faut au moins un objet clairement parlant.
 */
const EXPRESSIONS_OBJET = Object.entries(MOTS_OBJET).map(([categorie, mots]) => [
  categorie,
  new RegExp(`(^|[^a-z0-9])(${mots.map(m => sansAccents(m).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})(?=$|[^a-z0-9])`)
]);

function categorieParObjets(compte) {
  const objets = [compte.sampleSubject, ...(compte.messages || []).map(m => m.subject)]
    .filter(Boolean).map(sansAccents);
  if (!objets.length) return null;
  let meilleure = null;
  let meilleurScore = 0;
  for (const [categorie, expression] of EXPRESSIONS_OBJET) {
    const score = objets.filter(o => expression.test(o)).length;
    if (score > meilleurScore) { meilleure = categorie; meilleurScore = score; }
  }
  return meilleure;
}

/**
 * Catégorie d'un service, du plus sûr au plus deviné : domaine connu, marque
 * connue, règles, mots du domaine, puis — si on connaît le compte — objets de
 * ses emails.
 */
function categoryFor(domain, compte) {
  const connue = categorieConnue(domain);
  if (connue !== DEFAULT_CATEGORY) return connue;
  return categorieParMotsDuDomaine(domain)
    || (compte && categorieParObjets(compte))
    || DEFAULT_CATEGORY;
}

function categorieConnue(domain) {
  const d = String(domain || '').toLowerCase();
  if (CATEGORY_DOMAINS[d]) return CATEGORY_DOMAINS[d];

  // On teste aussi le domaine « racine » (sous-domaine retiré) : mail.truc.fr -> truc.fr
  const parts = d.split('.');
  if (parts.length > 2) {
    const root = parts.slice(-2).join('.');
    if (CATEGORY_DOMAINS[root]) return CATEGORY_DOMAINS[root];
  }

  // Marque connue : paypal.fr hérite de paypal.com, et sg.fr hérite de
  // societegenerale.fr via les alias. On réutilise la même clé que le
  // regroupement, sinon les deux mécanismes divergeraient.
  const brand = brandKey(d);
  if (CATEGORY_BY_BRAND[brand]) return CATEGORY_BY_BRAND[brand];

  for (const rule of CATEGORY_RULES) {
    if (rule.contains && d.includes(rule.contains)) return rule.category;
    if (rule.startsWith && d.startsWith(rule.startsWith)) return rule.category;
    if (rule.endsWith && d.endsWith(rule.endsWith)) return rule.category;
  }
  return DEFAULT_CATEGORY;
}

/**
 * Le domaine lui-même puis ses domaines parents : compte.zara.fr est couvert
 * par une fiche qui ne connaît que zara.fr.
 */
function candidatsDomaine(domain) {
  const parts = String(domain || '').toLowerCase().split('.').filter(Boolean);
  const out = [];
  while (parts.length >= 2) {
    out.push(parts.join('.'));
    parts.shift();
  }
  return out;
}

/**
 * Corrections saisies dans l'interface. Elles vivent dans data/, à part de
 * services.json : une mise à jour du fichier livré ne les écrase donc jamais.
 * Le fichier est relu à chaque écriture seulement (serveur mono-utilisateur).
 */
let localKnowledgeCache = null;
function localKnowledge() {
  if (localKnowledgeCache) return localKnowledgeCache;
  try {
    const brut = JSON.parse(fs.readFileSync(LOCAL_KNOWLEDGE_FILE, 'utf8'));
    localKnowledgeCache = { deletionLinks: brut.deletionLinks || {} };
  } catch (err) {
    localKnowledgeCache = { deletionLinks: {} };
  }
  return localKnowledgeCache;
}

function writeLocalKnowledge(data) {
  fs.mkdirSync(path.dirname(LOCAL_KNOWLEDGE_FILE), { recursive: true });
  const tmp = LOCAL_KNOWLEDGE_FILE + '.tmp';
  fs.writeFileSync(tmp, JSON.stringify(data, null, 2), 'utf8');
  fs.renameSync(tmp, LOCAL_KNOWLEDGE_FILE);
  localKnowledgeCache = { deletionLinks: data.deletionLinks || {} };
}

/**
 * Où gérer ou supprimer son compte, de la source la plus sûre à la plus
 * approximative :
 *   1. la correction saisie par l'utilisateur (elle prime toujours) ;
 *   2. services.json, tenu à la main pour les services fréquents en France ;
 *   3. la base JustDeleteMe importée, qui donne aussi la difficulté ;
 *   4. faute de mieux, une recherche web pré-remplie.
 */
function deletionInfoFor(domain) {
  const candidats = candidatsDomaine(domain);
  const perso = localKnowledge().deletionLinks;

  for (const c of candidats) {
    if (perso[c]) return { url: perso[c], source: 'perso', domaine: c };
  }
  for (const c of candidats) {
    if (DELETION_LINKS[c]) return { url: DELETION_LINKS[c], source: 'scanyt', domaine: c };
  }
  for (const c of candidats) {
    const index = JDM.domaines[c];
    const fiche = index === undefined ? null : JDM.fiches[index];
    if (fiche) {
      return {
        url: fiche.url,
        source: 'justdeleteme',
        domaine: c,
        difficulte: fiche.difficulte || null,
        note: fiche.note || '',
        nom: fiche.nom || ''
      };
    }
  }

  // Aucun lien connu : on renvoie vers une recherche assistée.
  const service = String(domain).split('.')[0];
  return {
    url: `https://www.google.com/search?q=${encodeURIComponent('supprimer mon compte ' + service)}`,
    source: 'recherche',
    domaine: String(domain)
  };
}

function deletionLinkFor(domain) {
  return deletionInfoFor(domain).url;
}

// ---------------------------------------------------------------------------
// Catégories renommées ou supprimées par l'utilisateur.
//
// Un simple renommage sur les services existants ne suffirait pas : la
// détection automatique réintroduirait l'ancien nom au scan suivant. On garde
// donc, par adresse email, une table « ancien nom -> nouveau nom », où la
// valeur null signifie « catégorie supprimée » (les services retombent alors
// dans la catégorie par défaut).
// ---------------------------------------------------------------------------
function readCategoryMap() {
  try {
    return JSON.parse(fs.readFileSync(CATEGORY_MAP_FILE, 'utf8'));
  } catch (err) {
    if (err.code === 'ENOENT') return {};
    throw err;
  }
}

function writeCategoryMap(data) {
  fs.mkdirSync(path.dirname(CATEGORY_MAP_FILE), { recursive: true });
  const tmp = CATEGORY_MAP_FILE + '.tmp';
  fs.writeFileSync(tmp, JSON.stringify(data, null, 2), 'utf8');
  fs.renameSync(tmp, CATEGORY_MAP_FILE);
}

function categoryMapFor(email) {
  const all = readCategoryMap();
  return (all[email] && typeof all[email] === 'object') ? all[email] : {};
}

/** Applique les renommages et suppressions à un nom de catégorie. */
function applyCategoryMap(category, map) {
  if (!Object.prototype.hasOwnProperty.call(map, category)) return category;
  return map[category] || DEFAULT_CATEGORY;
}

function renameCategory(email, from, to) {
  const all = readCategoryMap();
  const map = categoryMapFor(email);
  // Les anciens noms qui pointaient vers « from » suivent le renommage, sinon
  // une deuxième modification casserait la chaîne.
  for (const [ancien, actuel] of Object.entries(map)) {
    if (actuel === from) map[ancien] = to;
  }
  map[from] = to;
  all[email] = map;
  writeCategoryMap(all);
  return map;
}

function deleteCategory(email, name) {
  const all = readCategoryMap();
  const map = categoryMapFor(email);
  for (const [ancien, actuel] of Object.entries(map)) {
    if (actuel === name) map[ancien] = null;
  }
  map[name] = null;
  all[email] = map;
  writeCategoryMap(all);
  return map;
}

// Liste de toutes les catégories connues, pour alimenter le menu de l'interface.
function allCategories(map = {}) {
  const set = new Set(Object.values(CATEGORY_DOMAINS));
  for (const rule of CATEGORY_RULES) if (rule.category) set.add(rule.category);
  set.add(DEFAULT_CATEGORY);
  const visibles = new Set();
  for (const nom of set) {
    const final = applyCategoryMap(nom, map);
    if (final) visibles.add(final);
  }
  for (const nouveau of Object.values(map)) if (nouveau) visibles.add(nouveau);
  visibles.add(DEFAULT_CATEGORY);
  return Array.from(visibles).sort((a, b) => a.localeCompare(b, 'fr'));
}

// Ajoute le lien de suppression et la catégorie affichée. La catégorie choisie
// à la main par l'utilisateur (champ `category`) prime sur la détection
// automatique ; `categoryAuto` permet à l'interface de proposer un retour
// à la détection automatique.
function decorate(account, map = {}) {
  const auto = applyCategoryMap(categoryFor(account.domain, account), map);
  const suppression = deletionInfoFor(account.domain);
  return {
    ...account,
    category: applyCategoryMap(account.category || auto, map),
    categoryAuto: auto,
    isCategoryManual: Boolean(account.category),
    confirmed: account.confirmed !== false,
    deletionLink: suppression.url,
    deletion: suppression,
    fuite: fuiteFor(account.domain)
  };
}

// ---------------------------------------------------------------------------
// Logos des services.
//
// Le navigateur ne contacte jamais l'extérieur lui-même : il demande le logo à
// ce serveur local, qui va le chercher une seule fois puis le met en cache dans
// data/logos/. Quand aucune source n'a de logo, on répond 404 et l'interface
// affiche l'initiale colorée du service.
//
// Les sources sont essayées de la moins intrusive à la plus efficace :
//   1. DuckDuckGo — cache d'icônes déjà constitué, le site n'est pas contacté.
//      Sa couverture est incomplète (Massimo Dutti, Bershka, Zara… absents).
//   2. L'icône haute définition du site (apple-touch-icon) — aucun tiers.
//   3. Le favicon du site — aucun tiers, mais souvent minuscule (16 px).
//   4. Google — le plus complet et toujours au moins 48 px, en dernier recours.
//
// On ne s'arrête pas à la première source qui répond : un favicon de 16 px
// affiché dans une pastille de 34 px est illisible (le « B » de Bershka, par
// exemple). On continue donc tant qu'aucune source n'a fourni au moins 48 px,
// et on garde la meilleure image trouvée.
// ---------------------------------------------------------------------------
const LOGO_CACHE_DIR = path.join(DATA_DIR, 'logos');
const LOGO_TIMEOUT_MS = 4000;
const LOGO_MAX_BYTES = 500 * 1024;

// Icône « globe » générique renvoyée par Google quand il ne connaît pas le
// domaine : à traiter comme une absence de logo, sinon tous les sites inconnus
// se ressembleraient. (Si Google la change un jour, on retombe simplement sur
// l'ancien comportement : un globe gris au lieu de l'initiale.)
const GOOGLE_PLACEHOLDER_SHA256 = '59bfe9bc385ad69f50793ce4a53397316d7a875a7148a63c16df9b674c6cda64';

// Taille jugée suffisante pour une pastille de 34 px (écrans à haute densité
// compris). En dessous, on va voir si une autre source fait mieux.
const LOGO_GOOD_SIZE = 48;
// Changer ce numéro invalide les logos déjà en cache (utile quand la façon de
// les choisir évolue, comme ici).
const LOGO_CACHE_VERSION = 5;

/**
 * Plus grande dimension d'une image, sans dépendance externe.
 * Renvoie 0 si le format n'est pas reconnu (l'image reste utilisable, mais
 * elle ne sera pas préférée à une autre).
 */
function imageSize(buf) {
  if (buf.length >= 24 && buf.readUInt32BE(0) === 0x89504e47) {
    return Math.max(buf.readUInt32BE(16), buf.readUInt32BE(20)); // PNG
  }
  if (buf.length >= 6 && buf.readUInt16LE(0) === 0 && buf.readUInt16LE(2) === 1) {
    // ICO : plusieurs tailles possibles, on retient la plus grande.
    let best = 0;
    const count = buf.readUInt16LE(4);
    for (let i = 0; i < count && 6 + i * 16 + 1 < buf.length; i++) {
      const w = buf[6 + i * 16] || 256;
      const h = buf[6 + i * 16 + 1] || 256;
      best = Math.max(best, w, h);
    }
    return best;
  }
  if (buf.length >= 10 && buf.slice(0, 3).toString('latin1') === 'GIF') {
    return Math.max(buf.readUInt16LE(6), buf.readUInt16LE(8));
  }
  if (buf.slice(0, 400).toString('latin1').toLowerCase().includes('<svg')) {
    return 1024; // vectoriel : net à toutes les tailles
  }
  return 0;
}

const VALID_DOMAIN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

function sha256(buffer) {
  return crypto.createHash('sha256').update(buffer).digest('hex');
}

async function fetchImage(url, { allowRedirect = true } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOGO_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      signal: controller.signal,
      redirect: allowRedirect ? 'follow' : 'manual',
      headers: { 'User-Agent': 'Mozilla/5.0 (Scanyt, local)' }
    });
    if (!res.ok) return null;
    const type = (res.headers.get('content-type') || '').toLowerCase();
    if (!type.startsWith('image/')) return null;
    const body = Buffer.from(await res.arrayBuffer());
    if (!body.length || body.length > LOGO_MAX_BYTES) return null;
    return { body, type: type.split(';')[0] };
  } catch (err) {
    return null; // hors ligne, délai dépassé, site injoignable : pas de logo
  } finally {
    clearTimeout(timer);
  }
}

// Un « no logo » vient parfois d'une simple coupure réseau : on le réessaie au
// bout d'une semaine plutôt que de laisser la case vide pour toujours.
const LOGO_NEGATIVE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Domaines à essayer pour un service, du plus précis au plus général :
 * infos.doctolib.com -> doctolib.com, acct.epicgames.com -> epicgames.com.
 * Beaucoup de services écrivent depuis un sous-domaine qui n'a pas d'icône
 * propre, alors que le domaine principal en a une.
 */
function logoCandidates(domain) {
  const parts = domain.split('.');
  const candidates = [domain];
  while (parts.length > 2) {
    parts.shift();
    candidates.push(parts.join('.'));
  }
  // Certains domaines ne servent qu'à envoyer des emails et n'ont aucun site
  // (redditmail.com, service-airfrance.com…) : services.json indique alors la
  // vraie marque, dont on ira chercher le logo.
  // L'alias passe DEVANT le domaine d'envoi : samsung-mail.com sert bien une
  // icône, mais ce n'est pas celle de Samsung. Quand services.json désigne la
  // vraie marque, c'est elle qui fait foi.
  const aliases = candidates.filter(c => LOGO_ALIASES[c]).map(c => LOGO_ALIASES[c]);
  return Array.from(new Set([...aliases, ...candidates])).slice(0, 4);
}

async function findLogo(domain) {
  for (const candidate of logoCandidates(domain)) {
    const found = await findLogoForDomain(candidate);
    if (found) return found;
  }
  return null;
}

/**
 * Cherche l'icône déclarée dans le HTML de la page d'accueil. Beaucoup de sites
 * ne servent rien sur /favicon.ico mais déclarent leur icône ailleurs
 * (<link rel="icon" href="/assets/logo-192.png">). Suivre la page d'accueil
 * règle aussi le cas des domaines qui ne répondent que sur www.
 */
async function findLogoInHtml(pageUrl) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOGO_TIMEOUT_MS);
  let html, finalUrl;
  try {
    const res = await fetch(pageUrl, {
      signal: controller.signal,
      headers: { 'User-Agent': 'Mozilla/5.0 (Scanyt, local)' }
    });
    if (!res.ok) return null;
    finalUrl = res.url || pageUrl;
    html = (await res.text()).slice(0, 200000); // l'en-tête suffit
  } catch (err) {
    return null;
  } finally {
    clearTimeout(timer);
  }

  const links = [];
  const re = /<link\b[^>]*>/gi;
  let tag;
  while ((tag = re.exec(html))) {
    const rel = (/rel\s*=\s*["']?([^"'>]+)/i.exec(tag[0]) || [])[1] || '';
    if (!/\bicon\b/i.test(rel)) continue;
    const href = (/href\s*=\s*["']([^"']+)/i.exec(tag[0]) || [])[1];
    if (!href) continue;
    const sizes = (/sizes\s*=\s*["']?(\d+)/i.exec(tag[0]) || [])[1];
    links.push({ href, score: /apple-touch/i.test(rel + href) ? 512 : Number(sizes) || 1 });
  }
  links.sort((a, b) => b.score - a.score);

  for (const link of links.slice(0, 3)) {
    let url;
    try { url = new URL(link.href, finalUrl).href; } catch (err) { continue; }
    const found = await fetchImage(url);
    if (found) return found;
  }
  return null;
}

async function findLogoForDomain(domain) {
  // Du moins intrusif au plus efficace. On s'arrête dès qu'une image est assez
  // nette (LOGO_GOOD_SIZE) ; sinon on garde la meilleure trouvée.
  const sources = [
    () => fetchImage(`https://icons.duckduckgo.com/ip3/${domain}.ico`),
    () => fetchImage(`https://${domain}/apple-touch-icon.png`),
    () => fetchImage(`https://${domain}/favicon.ico`),
    () => fetchImage(`https://www.${domain}/apple-touch-icon.png`),
    () => fetchImage(`https://www.${domain}/favicon.ico`),
    () => findLogoInHtml(`https://${domain}/`),
    () => findLogoInHtml(`https://www.${domain}/`),
    () => fetchImage(`https://www.google.com/s2/favicons?domain=${domain}&sz=64`)
  ];

  let best = null;
  let bestSize = -1;
  for (const source of sources) {
    const found = await source();
    if (!found || sha256(found.body) === GOOGLE_PLACEHOLDER_SHA256) continue;
    const size = imageSize(found.body);
    if (size >= LOGO_GOOD_SIZE) return found; // assez net, inutile d'aller plus loin
    if (size > bestSize) { best = found; bestSize = size; }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Sauvegarde locale : un seul fichier JSON, indexé par adresse email.
// Le mot de passe n'y est jamais écrit — seuls les résultats du scan le sont.
// ---------------------------------------------------------------------------
function readAll() {
  try {
    return JSON.parse(fs.readFileSync(DATA_FILE, 'utf8'));
  } catch (err) {
    if (err.code === 'ENOENT') return {};
    // Fichier corrompu : on ne l'écrase pas silencieusement, on remonte l'erreur.
    throw err;
  }
}

function writeAll(data) {
  fs.mkdirSync(path.dirname(DATA_FILE), { recursive: true });
  const tmp = DATA_FILE + '.tmp';
  fs.writeFileSync(tmp, JSON.stringify(data, null, 2), 'utf8');
  fs.renameSync(tmp, DATA_FILE);
}

// Nombre d'emails détaillés conservés par service (même plafond que le scanner).
const MAX_MESSAGES_PER_SERVICE = 50;

/**
 * Réunit les emails déjà connus et ceux du nouveau scan, sans doublon, du plus
 * récent au plus ancien. Un scan limité dans le temps ne fait donc pas perdre
 * les emails plus anciens détectés lors d'un scan précédent.
 */
function mergeMessages(previous, incoming) {
  const byKey = new Map();
  for (const m of [...(incoming || []), ...(previous || [])]) {
    const key = m.messageId || `${m.date}|${m.subject}|${m.from}`;
    if (!byKey.has(key)) byKey.set(key, m);
  }
  return Array.from(byKey.values())
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))
    .slice(0, MAX_MESSAGES_PER_SERVICE);
}

/**
 * Fusionne les résultats d'un nouveau scan avec les entrées déjà stockées, en
 * conservant le statut, les notes et la catégorie déjà saisis par l'utilisateur.
 */
// ---------------------------------------------------------------------------
// Services ignorés : liste noire par adresse email, dans son propre fichier
// pour ne pas se mélanger aux résultats de scan. Un service ignoré est retiré
// de la liste ET ne peut plus y revenir, même si de nouveaux emails arrivent.
// ---------------------------------------------------------------------------
function readIgnored() {
  try {
    return JSON.parse(fs.readFileSync(IGNORED_FILE, 'utf8'));
  } catch (err) {
    if (err.code === 'ENOENT') return {};
    throw err;
  }
}

function writeIgnored(data) {
  fs.mkdirSync(path.dirname(IGNORED_FILE), { recursive: true });
  const tmp = IGNORED_FILE + '.tmp';
  fs.writeFileSync(tmp, JSON.stringify(data, null, 2), 'utf8');
  fs.renameSync(tmp, IGNORED_FILE);
}

function ignoredFor(email) {
  const all = readIgnored();
  return Array.isArray(all[email]) ? all[email] : [];
}

/**
 * Un domaine est ignoré s'il figure dans la liste, ou s'il en est un
 * sous-domaine : ignorer « uniqlo.com » masque aussi « ml.store.uniqlo.com ».
 */
function isIgnored(list, domain) {
  return list.some(ign => domain === ign || domain.endsWith('.' + ign));
}

function mergeScanResults(email, scanResults) {
  const all = readAll();
  const existing = all[email] || {};
  const ignored = ignoredFor(email);
  // Un service masqué qui réécrit disparaît sans bruit : sans ce décompte, on
  // se demande pourquoi il ne réapparaît pas après un nouveau scan.
  const bilan = { nouveaux: 0, connus: 0, masques: 0 };
  // Date de ce scan, la même pour tous les services qu'il voit : un service
  // découvert à cette date est « nouveau depuis le scan précédent ». Au tout
  // premier scan, tout serait nouveau : rien n'est marqué.
  const maintenant = new Date().toISOString();
  const premierScan = Object.keys(existing).length === 0;

  for (const item of scanResults) {
    // Service mis en liste noire : on l'écarte, même s'il vient d'écrire.
    if (isIgnored(ignored, item.domain)) { bilan.masques += 1; continue; }
    const prev = existing[item.domain];
    if (prev) bilan.connus += 1; else bilan.nouveaux += 1;
    existing[item.domain] = {
      domain: item.domain,
      serviceName: item.serviceName,
      firstSeen: prev && prev.firstSeen && prev.firstSeen < item.firstSeen ? prev.firstSeen : item.firstSeen,
      lastSeen: item.lastSeen,
      count: item.count,
      sampleSubject: item.sampleSubject,
      // Une fois repérée, une newsletter le reste : un scan sans l'option
      // activée ne doit pas effacer l'information.
      newsletter: Boolean(item.newsletter || (prev && prev.newsletter)),
      // Un compte confirmé le reste : un scan qui ne repasse pas sur l'email
      // d'inscription ne doit pas le rétrograder en « présumé ». Les comptes
      // enregistrés avant l'arrivée de ce champ viennent tous d'un scan par
      // mots-clés : ils sont confirmés par construction.
      confirmed: Boolean(item.confirmed || (prev && prev.confirmed !== false)),
      unsubscribe: item.unsubscribe || (prev && prev.unsubscribe) || null,
      // « En un clic » suit le lien qu'on garde : celui du scan s'il en a un.
      unsubscribeOneClick: item.unsubscribe ? Boolean(item.unsubscribeOneClick) : Boolean(prev && prev.unsubscribeOneClick),
      unsubscribeMailto: item.unsubscribeMailto || (prev && prev.unsubscribeMailto) || null,
      newsletterCount: Math.max(Number(item.newsletterCount) || 0, Number(prev && prev.newsletterCount) || 0),
      // Le dernier scan dit ce qui est lu MAINTENANT : il remplace l'ancien.
      newsletterUnread: item.newsletterUnread !== undefined ? Number(item.newsletterUnread) || 0 : Number(prev && prev.newsletterUnread) || 0,
      lastNewsletter: [item.lastNewsletter, prev && prev.lastNewsletter].filter(Boolean).sort().pop() || null,
      unsubscribedAt: (prev && prev.unsubscribedAt) || null,
      rgpdRequestedAt: (prev && prev.rgpdRequestedAt) || null,
      discoveredAt: prev ? prev.discoveredAt || null : premierScan ? null : maintenant,
      lastScanAt: maintenant,
      messages: mergeMessages(prev && prev.messages, item.messages),
      status: (prev && prev.status) || 'a_trier',
      notes: (prev && prev.notes) || '',
      category: (prev && prev.category) || null,
      updatedAt: new Date().toISOString()
    };
  }

  all[email] = existing;
  writeAll(all);
  return { comptes: existing, bilan };
}

/**
 * Moyens de se désinscrire d'une ligne qui réunit plusieurs domaines : le lien
 * « en un clic » d'abord, puis une page web, puis un email ; les envois de
 * newsletter s'additionnent et la date retenue est la plus récente.
 */
function meilleureDesinscription(membres) {
  const lien = membres.find(m => m.unsubscribeOneClick && /^https?:/i.test(m.unsubscribe || ''))
    || membres.find(m => /^https?:/i.test(m.unsubscribe || ''))
    || membres.find(m => m.unsubscribe) || {};
  const dates = membres.map(m => m.lastNewsletter).filter(Boolean).sort();
  return {
    unsubscribe: lien.unsubscribe || null,
    unsubscribeOneClick: Boolean(lien.unsubscribeOneClick),
    unsubscribeMailto: (membres.find(m => m.unsubscribeMailto) || {}).unsubscribeMailto || null,
    newsletterCount: membres.reduce((total, m) => total + (Number(m.newsletterCount) || 0), 0),
    newsletterUnread: membres.reduce((total, m) => total + (Number(m.newsletterUnread) || 0), 0),
    lastNewsletter: dates.pop() || null
  };
}

function unignoreDomain(email, domain) {
  const all = readIgnored();
  const list = Array.isArray(all[email]) ? all[email] : [];
  all[email] = list.filter(d => d !== domain);
  writeIgnored(all);
  return all[email];
}

function appliquerChamps(entree, { status, notes, category, unsubscribed, rgpdRequested }) {
  if (status !== undefined) entree.status = status;
  if (notes !== undefined) entree.notes = notes;
  // Scanyt ne peut pas constater une désinscription : c'est le clic sur le
  // lien qui la déclare, et l'utilisateur peut revenir dessus.
  if (unsubscribed !== undefined) {
    entree.unsubscribedAt = unsubscribed ? new Date().toISOString() : null;
  }
  // Demande d'effacement (RGPD) : de même, c'est l'utilisateur qui la déclare
  // envoyée ; la date fait courir le délai de réponse d'un mois.
  if (rgpdRequested !== undefined) {
    entree.rgpdRequestedAt = rgpdRequested ? new Date().toISOString() : null;
  }
  // category vide (ou null) = retour à la détection automatique.
  if (category !== undefined) entree.category = category ? String(category).trim() : null;
  entree.updatedAt = new Date().toISOString();
  return entree;
}

function updateAccount(email, domain, champs) {
  const all = readAll();
  if (!all[email] || !all[email][domain]) throw new Error('Entrée introuvable');
  appliquerChamps(all[email][domain], champs);
  writeAll(all);
  return all[email][domain];
}

// ---------------------------------------------------------------------------
// Actions groupées.
//
// Chaque helper fait UNE seule lecture et UNE seule écriture, quel que soit le
// nombre de services : vingt appels unitaires réécriraient accounts.json vingt
// fois de suite, avec le risque que deux écritures se chevauchent.
// ---------------------------------------------------------------------------
function updateAccounts(email, domains, champs) {
  const all = readAll();
  const boite = all[email] || {};
  let touches = 0;
  for (const domain of domains) {
    if (!boite[domain]) continue;
    appliquerChamps(boite[domain], champs);
    touches += 1;
  }
  if (touches) writeAll(all);
  return touches;
}

function deleteAccounts(email, domains) {
  const all = readAll();
  const boite = all[email];
  if (!boite) return 0;
  let retires = 0;
  for (const domain of domains) {
    if (!boite[domain]) continue;
    delete boite[domain];
    retires += 1;
  }
  if (retires) writeAll(all);
  return retires;
}

function ignoreDomains(email, domains) {
  const all = readIgnored();
  const list = Array.isArray(all[email]) ? all[email] : [];
  for (const domain of domains) {
    if (!list.includes(domain)) list.push(domain);
  }
  all[email] = list.sort();
  writeIgnored(all);
  deleteAccounts(email, domains); // on les retire aussi de la liste courante
  return all[email];
}

// ---------------------------------------------------------------------------
// Jobs de scan : le scan tourne en tâche de fond et le front interroge sa
// progression, ce qui permet d'afficher une vraie barre de chargement.
// ---------------------------------------------------------------------------
const jobs = new Map();
const JOB_TTL_MS = 15 * 60 * 1000;

function cleanupJobs() {
  const now = Date.now();
  for (const [id, job] of jobs) {
    if (now - job.createdAt > JOB_TTL_MS) jobs.delete(id);
  }
}

/**
 * Avance douce vers un plafond, sans jamais l'atteindre : sert pendant les
 * phases où le serveur IMAP ne renvoie aucun signal (connexion, recherche).
 * Au bout d'une demi-vie, la moitié du chemin restant est parcourue.
 */
function creep(elapsedMs, from, to, halfLifeMs) {
  return from + (to - from) * (1 - Math.pow(0.5, elapsedMs / halfLifeMs));
}

/**
 * Traduit l'avancement du scanner en pourcentage.
 *
 * Deux principes pour éviter les à-coups :
 *  - pendant une phase sans retour (la recherche IMAP peut durer une minute
 *    sans rien émettre), la barre progresse avec le temps écoulé ;
 *  - la phase suivante repart EXACTEMENT d'où la précédente s'est arrêtée,
 *    donc aucun bond, même si la recherche a été très rapide.
 * La valeur ne redescend jamais.
 *
 * Le suivi est réévalué à chaque interrogation du front, et non à chaque
 * événement du scanner : c'est ce qui permet à la barre de bouger pendant
 * les longues attentes silencieuses.
 */
function makeProgressTracker() {
  let phaseKey = null;
  let phaseStart = Date.now();
  let phaseFrom = 0;
  let last = 0;
  let fetchBase = null;

  return function track(info) {
    if (!info || !info.phase) return { percent: Math.round(last), label: 'Préparation…' };

    const key = `${info.mode || ''}|${info.phase}|${info.folderIndex || 0}`;
    if (key !== phaseKey) {
      phaseKey = key;
      phaseStart = Date.now();
      // Origine figée au début de la phase : sinon la courbe se recalcule à
      // partir de sa propre valeur à chaque appel et sature en quelques
      // secondes au lieu de monter progressivement.
      phaseFrom = last;
    }
    const elapsed = Date.now() - phaseStart;

    let percent = last;
    let label = '';

    if (info.phase === 'connecting') {
      percent = creep(elapsed, phaseFrom, 7, 3000);
      label = 'Connexion à la boîte mail…';

    } else if (info.mode === 'gmail') {
      if (info.phase === 'searching') {
        // Demi-vie longue : la barre continue de monter visiblement même sur
        // une recherche de plusieurs minutes, sans jamais atteindre le plafond.
        percent = creep(elapsed, phaseFrom, 60, 40000);
        label = 'Recherche dans tous les messages…';
      } else if (info.phase === 'dating') {
        const total = info.messagesTotal || 1;
        const done = info.messagesProcessed || 0;
        percent = phaseFrom + (70 - phaseFrom) * (done / total);
        label = `Répartition par année : ${done.toLocaleString('fr-FR')} / ${total.toLocaleString('fr-FR')}`;
      } else {
        const total = info.messagesTotal || 1;
        const done = info.messagesProcessed || 0;
        // On repart du point atteint par la recherche : progression continue.
        if (fetchBase === null) fetchBase = last;
        percent = fetchBase + (98 - fetchBase) * (done / total);
        label = `Analyse des messages : ${done.toLocaleString('fr-FR')} / ${total.toLocaleString('fr-FR')}`;
      }

    } else {
      const foldersTotal = info.foldersTotal || 1;
      const folderIndex = info.folderIndex || 0;
      let inFolder = 0;
      label = `Dossier ${folderIndex + 1}/${foldersTotal} — ${info.folderName || ''}`;

      if (info.phase === 'searching' && info.termsTotal) {
        inFolder = 0.5 * (info.termsDone / info.termsTotal);
        label += ' · recherche';
      } else if (info.phase === 'dating') {
        const total = info.messagesTotal || 1;
        inFolder = 0.5 + 0.1 * ((info.messagesProcessed || 0) / total);
        label += ' · répartition par année';
      } else if (info.phase === 'fetching') {
        const total = info.messagesTotal || 1;
        const done = info.messagesProcessed || 0;
        inFolder = 0.6 + 0.4 * (done / total);
        label += ` · analyse ${done}/${total}`;
      }
      percent = 7 + ((folderIndex + inFolder) / foldersTotal) * 91;
      // Si un dossier reste longtemps muet (une recherche IMAP peut bloquer),
      // on avance quand même, sans jamais dépasser la fin du dossier en cours.
      const folderEnd = 7 + ((folderIndex + 1) / foldersTotal) * 91;
      percent = Math.max(percent, creep(elapsed, phaseFrom, folderEnd, 40000));
    }

    last = Math.max(last, Math.min(98, percent));
    return { percent: Math.round(last), label };
  };
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------
const app = express();
// L'import d'une sauvegarde a sa propre limite, bien plus large (voir /api/import).
const lireJson = express.json({ limit: '200kb' });
app.use((req, res, next) => (req.path === '/api/import' ? next() : lireJson(req, res, next)));
app.use(express.static(path.join(__dirname, 'public')));

app.get('/api/providers', (req, res) => {
  res.json(PROVIDERS);
});

app.get('/api/categories', (req, res) => {
  const email = String(req.query.email || '').trim().toLowerCase();
  res.json(allCategories(email ? categoryMapFor(email) : {}));
});

// Renomme une catégorie partout : services concernés et détection automatique.
app.post('/api/categories/rename', (req, res) => {
  const { email, from, to } = req.body || {};
  if (!email || !from || !to) return res.status(400).json({ error: 'email, from et to requis.' });
  const nom = String(to).trim();
  if (!nom) return res.status(400).json({ error: 'Le nouveau nom ne peut pas être vide.' });
  const mail = String(email).toLowerCase();
  renameCategory(mail, String(from), nom);
  res.json({ categories: allCategories(categoryMapFor(mail)) });
});

// Supprime une catégorie : ses services retombent dans la catégorie par défaut.
app.delete('/api/categories', (req, res) => {
  const { email, name } = req.body || {};
  if (!email || !name) return res.status(400).json({ error: 'email et name requis.' });
  if (String(name) === DEFAULT_CATEGORY) {
    return res.status(400).json({ error: `La catégorie « ${DEFAULT_CATEGORY} » ne peut pas être supprimée.` });
  }
  const mail = String(email).toLowerCase();
  deleteCategory(mail, String(name));
  res.json({ categories: allCategories(categoryMapFor(mail)) });
});

// Corrige le lien « Gérer » d'un service. La correction est enregistrée dans
// data/knowledge.local.json et prime sur services.json comme sur la base
// importée. Un lien vide efface la correction et rend la main à la détection.
app.post('/api/knowledge/deletion-link', (req, res) => {
  const domaine = String((req.body || {}).domain || '').toLowerCase().trim();
  const url = String((req.body || {}).url || '').trim();

  if (!VALID_DOMAIN.test(domaine) || domaine.length > 255) {
    return res.status(400).json({ error: 'Domaine invalide.' });
  }

  const data = { deletionLinks: { ...localKnowledge().deletionLinks } };
  if (!url) {
    delete data.deletionLinks[domaine];
  } else {
    // Seules des pages web sont acceptées : c'est ce que le bouton ouvrira.
    let propre;
    try {
      propre = new URL(url);
    } catch (err) {
      return res.status(400).json({ error: "Ce n'est pas une adresse valide (elle doit commencer par https://)." });
    }
    if (propre.protocol !== 'https:' && propre.protocol !== 'http:') {
      return res.status(400).json({ error: 'Seules les adresses http:// et https:// sont acceptées.' });
    }
    if (propre.href.length > 2000) return res.status(400).json({ error: 'Adresse trop longue.' });
    data.deletionLinks[domaine] = propre.href;
  }

  try {
    writeLocalKnowledge(data);
  } catch (err) {
    return res.status(500).json({ error: "Impossible d'enregistrer la correction." });
  }
  res.json({ domain: domaine, deletion: deletionInfoFor(domaine) });
});

app.get('/api/logo/:domain', async (req, res) => {
  const domain = String(req.params.domain || '').toLowerCase();
  // Le domaine sert de nom de fichier dans le cache : on le valide strictement.
  if (!VALID_DOMAIN.test(domain) || domain.length > 255) return res.status(400).end();

  const cacheFile = path.join(LOGO_CACHE_DIR, domain);
  const metaFile = cacheFile + '.json';

  try {
    const meta = JSON.parse(fs.readFileSync(metaFile, 'utf8'));
    if (meta.v !== LOGO_CACHE_VERSION) throw { code: 'ENOENT' }; // cache périmé
    if (!meta.type) {
      // Absence de logo : on ne réinterroge pas le réseau avant expiration.
      if (Date.now() - (meta.at || 0) < LOGO_NEGATIVE_TTL_MS) return res.status(404).end();
      throw { code: 'ENOENT' };
    }
    res.set('Content-Type', meta.type);
    res.set('Cache-Control', 'max-age=86400');
    return res.send(fs.readFileSync(cacheFile));
  } catch (err) {
    if (err.code !== 'ENOENT') return res.status(500).end();
  }

  const logo = await findLogo(domain);
  try {
    fs.mkdirSync(LOGO_CACHE_DIR, { recursive: true });
    if (logo) fs.writeFileSync(cacheFile, logo.body);
    fs.writeFileSync(metaFile, JSON.stringify({
      v: LOGO_CACHE_VERSION,
      type: logo ? logo.type : null,
      size: logo ? imageSize(logo.body) : 0,
      at: Date.now()
    }));
  } catch (err) {
    // Cache impossible (disque plein, droits) : on sert quand même le logo.
  }

  if (!logo) return res.status(404).end();
  res.set('Content-Type', logo.type);
  res.set('Cache-Control', 'max-age=86400');
  res.send(logo.body);
});

app.get('/api/accounts', (req, res) => {
  const email = String(req.query.email || '').trim().toLowerCase();
  if (!email) return res.status(400).json({ error: 'Paramètre email requis.' });
  const ignored = ignoredFor(email);
  const map = categoryMapFor(email);
  const accounts = groupAccounts(
    Object.values(readAll()[email] || {}).filter(a => !isIgnored(ignored, a.domain)),
    manualGroupsFor(email)
  ).map(a => decorate(a, map));
  res.json({ email, accounts, ignored });
});

/**
 * Une action groupée porte sur des LIGNES du tableau, or une ligne peut réunir
 * plusieurs domaines (proton.me + notify.proton.me). On déplie donc chaque
 * ligne en ses domaines réels, sans doublon.
 *
 * Accepte `domain` (une ligne) ou `domains` (plusieurs). Renvoie null si la
 * requête ne désigne rien d'exploitable.
 */
const LOT_MAX = 500;
function domainesVises(email, corps) {
  const brut = Array.isArray(corps.domains)
    ? corps.domains
    : (corps.domain ? [corps.domain] : []);
  if (!brut.length || brut.length > LOT_MAX) return null;
  const vus = new Set();
  for (const d of brut) {
    if (typeof d !== 'string' || !d.trim()) continue;
    for (const membre of groupMembers(email, d.trim().toLowerCase())) vus.add(membre);
  }
  return vus.size ? [...vus] : null;
}

// ---------------------------------------------------------------------------
// Effacement.
//
// Transmettre le dossier à quelqu'un ne doit pas transmettre ses données. Une
// consigne écrite dans un mode d'emploi serait le maillon faible d'une promesse
// de confidentialité : ce bouton fait le ménage lui-même.
// ---------------------------------------------------------------------------
const FICHIERS_PERSONNELS = [
  DATA_FILE, IGNORED_FILE, CATEGORY_MAP_FILE, GROUPS_FILE, LOCAL_KNOWLEDGE_FILE
];

app.delete('/api/data', (req, res) => {
  // Un jeton explicite : ni un clic de travers ni une requête égarée ne doivent
  // pouvoir effacer le travail de tri.
  if ((req.body || {}).confirm !== 'EFFACER') {
    return res.status(400).json({ error: "Confirmation manquante." });
  }

  const efface = [];
  const echecs = [];

  for (const fichier of FICHIERS_PERSONNELS) {
    try {
      fs.rmSync(fichier, { force: true });
      fs.rmSync(fichier + '.tmp', { force: true }); // reste d'une écriture interrompue
      efface.push(path.basename(fichier));
    } catch (err) {
      echecs.push(path.basename(fichier));
    }
  }

  // Le cache de logos est lui aussi personnel : il dit quels services on
  // fréquente. Il se reconstituera tout seul au prochain affichage.
  let logos = 0;
  try {
    logos = fs.existsSync(LOGO_CACHE_DIR) ? fs.readdirSync(LOGO_CACHE_DIR).length : 0;
    fs.rmSync(LOGO_CACHE_DIR, { recursive: true, force: true });
  } catch (err) {
    echecs.push('logos');
  }

  // Les caches en mémoire doivent partir avec les fichiers, sinon le serveur
  // continuerait de servir ce qu'il vient d'effacer.
  localKnowledgeCache = null;
  jobs.clear();

  if (echecs.length) {
    return res.status(500).json({ error: `Effacement incomplet : ${echecs.join(', ')}.` });
  }
  res.json({ ok: true, fichiers: efface.length, logos });
});

// ---------------------------------------------------------------------------
// Export.
//
// Le tri (statuts, notes, catégories) représente du travail : il ne doit pas
// dépendre de la survie d'un seul fichier dans data/. Le JSON est une
// sauvegarde complète, relisible ; le CSV s'ouvre dans un tableur.
// ---------------------------------------------------------------------------
const EXPORT_COLONNES = [
  ['serviceName', 'Service'],
  ['domain', 'Domaine'],
  ['domainsJoint', 'Autres domaines'],
  ['category', 'Catégorie'],
  ['status', 'Statut'],
  ['notes', 'Notes'],
  ['firstSeen', 'Premier email'],
  ['lastSeen', 'Dernier email'],
  ['count', 'Emails'],
  ['newsletterOui', 'Newsletter'],
  ['confirmedLibelle', 'Certitude'],
  ['unsubscribedAt', 'Désabonné le'],
  ['rgpdRequestedAt', 'Demande RGPD le'],
  ['deletionLink', 'Page de gestion'],
  ['deletionDifficulte', 'Difficulté']
];

const STATUTS_LISIBLES = {
  a_trier: 'À trier', a_garder: 'À garder', a_supprimer: 'À supprimer', supprime: 'Supprimé'
};

function ligneExport(acc) {
  const autres = (acc.domains || []).filter(d => d !== acc.domain);
  return {
    ...acc,
    domainsJoint: autres.join(' '),
    status: STATUTS_LISIBLES[acc.status] || acc.status || '',
    newsletterOui: acc.newsletter ? 'oui' : 'non',
    confirmedLibelle: acc.confirmed === false ? 'présumé' : 'confirmé',
    deletionDifficulte: (acc.deletion && acc.deletion.difficulte) || ''
  };
}

/**
 * Une cellule CSV. Le séparateur est le point-virgule : c'est ce qu'attendent
 * Excel et Numbers en configuration française, où la virgule est le séparateur
 * décimal.
 */
function celluleCsv(valeur) {
  const texte = valeur === null || valeur === undefined ? '' : String(valeur);
  return /[";\r\n]/.test(texte) ? `"${texte.replace(/"/g, '""')}"` : texte;
}

function versCsv(accounts) {
  const lignes = [EXPORT_COLONNES.map(([, titre]) => celluleCsv(titre)).join(';')];
  for (const acc of accounts) {
    const plat = ligneExport(acc);
    lignes.push(EXPORT_COLONNES.map(([cle]) => celluleCsv(plat[cle])).join(';'));
  }
  // Le BOM évite que les accents ressortent en mojibake à l'ouverture dans Excel.
  return '\ufeff' + lignes.join('\r\n') + '\r\n';
}

app.get('/api/export', (req, res) => {
  const email = String(req.query.email || '').trim().toLowerCase();
  if (!email) return res.status(400).json({ error: 'Paramètre email requis.' });
  const format = req.query.format === 'json' ? 'json' : 'csv';

  const ignored = ignoredFor(email);
  const map = categoryMapFor(email);
  const accounts = groupAccounts(
    Object.values(readAll()[email] || {}).filter(a => !isIgnored(ignored, a.domain)),
    manualGroupsFor(email)
  ).map(a => decorate(a, map));

  const jour = new Date().toISOString().slice(0, 10);
  // Le nom de fichier ne doit contenir ni accent ni caractère douteux : il part
  // dans un en-tête HTTP.
  const base = `scanyt-${email.replace(/[^a-z0-9]+/g, '-')}-${jour}`;

  if (format === 'json') {
    res.set('Content-Type', 'application/json; charset=utf-8');
    res.set('Content-Disposition', `attachment; filename="${base}.json"`);
    return res.send(JSON.stringify({
      _outil: 'Scanyt',
      _version: 2,
      _exporteLe: new Date().toISOString(),
      email,
      // Vue lisible : une ligne par service, comme à l'écran.
      accounts,
      ignored,
      groups: manualGroupsFor(email),
      categories: categoryMapFor(email),
      // Les données telles qu'enregistrées, domaine par domaine : c'est ce
      // que relit l'import, sans perte. `accounts` réunit plusieurs domaines
      // sur une ligne et ne permettrait pas de les séparer exactement.
      stockage: readAll()[email] || {},
      deletionLinks: localKnowledge().deletionLinks
    }, null, 2));
  }

  res.set('Content-Type', 'text/csv; charset=utf-8');
  res.set('Content-Disposition', `attachment; filename="${base}.csv"`);
  res.send(versCsv(accounts));
});

// ---------------------------------------------------------------------------
// Import d'une sauvegarde JSON.
//
// Sert à retrouver son tri sur un autre appareil, ou après un effacement.
// L'import FUSIONNE avec ce qui existe déjà, il n'écrase rien : pour chaque
// service, les emails des deux côtés sont réunis, et le statut, la note et la
// catégorie retenus sont ceux modifiés le plus récemment.
// ---------------------------------------------------------------------------
const STATUTS_VALIDES = new Set(Object.keys(STATUTS_LISIBLES));
const MESSAGES_IMPORT_MAX = 50;

function texteOuNull(v, max = 2000) {
  return typeof v === 'string' && v ? v.slice(0, max) : null;
}

function dateOuNull(v) {
  return typeof v === 'string' && !Number.isNaN(Date.parse(v)) ? new Date(v).toISOString() : null;
}

/** Une entrée de sauvegarde, réduite aux champs connus et vérifiée. */
function entreePropre(brute) {
  if (!brute || typeof brute !== 'object') return null;
  const domain = String(brute.domain || '').toLowerCase();
  if (!VALID_DOMAIN.test(domain) || domain.length > 255) return null;
  const messages = (Array.isArray(brute.messages) ? brute.messages : [])
    .filter(m => m && typeof m === 'object')
    .slice(0, MESSAGES_IMPORT_MAX)
    .map(m => ({
      subject: texteOuNull(m.subject, 500) || '',
      date: dateOuNull(m.date),
      from: texteOuNull(m.from, 320) || '',
      fromName: texteOuNull(m.fromName, 200) || '',
      messageId: texteOuNull(m.messageId, 500)
    }));
  return {
    domain,
    serviceName: texteOuNull(brute.serviceName, 200) || serviceNameFromDomain(domain),
    firstSeen: dateOuNull(brute.firstSeen),
    lastSeen: dateOuNull(brute.lastSeen),
    count: Math.max(0, Math.min(1e7, Math.round(Number(brute.count) || messages.length))),
    sampleSubject: texteOuNull(brute.sampleSubject, 500) || '',
    newsletter: Boolean(brute.newsletter),
    confirmed: brute.confirmed !== false,
    unsubscribe: texteOuNull(brute.unsubscribe),
    unsubscribeOneClick: Boolean(brute.unsubscribeOneClick),
    unsubscribeMailto: texteOuNull(brute.unsubscribeMailto),
    newsletterCount: Math.max(0, Math.min(1e7, Math.round(Number(brute.newsletterCount) || 0))),
    newsletterUnread: Math.max(0, Math.min(1e7, Math.round(Number(brute.newsletterUnread) || 0))),
    lastNewsletter: dateOuNull(brute.lastNewsletter),
    unsubscribedAt: dateOuNull(brute.unsubscribedAt),
    rgpdRequestedAt: dateOuNull(brute.rgpdRequestedAt),
    discoveredAt: dateOuNull(brute.discoveredAt),
    lastScanAt: dateOuNull(brute.lastScanAt),
    messages,
    status: STATUTS_VALIDES.has(brute.status) ? brute.status : 'a_trier',
    notes: texteOuNull(brute.notes, 5000) || '',
    category: texteOuNull(brute.category, 100),
    updatedAt: dateOuNull(brute.updatedAt)
  };
}

/**
 * Les entrées d'une sauvegarde, domaine par domaine. Les sauvegardes d'avant
 * la version 2 n'ont que les lignes regroupées : on répartit alors les
 * emails de chaque ligne entre ses domaines d'après leur expéditeur.
 */
function entreesDeSauvegarde(sauvegarde) {
  if (sauvegarde.stockage && typeof sauvegarde.stockage === 'object') {
    return Object.values(sauvegarde.stockage).map(entreePropre).filter(Boolean);
  }
  const entrees = [];
  for (const ligne of Array.isArray(sauvegarde.accounts) ? sauvegarde.accounts : []) {
    if (!ligne || typeof ligne !== 'object') continue;
    const domaines = Array.isArray(ligne.domains) && ligne.domains.length ? ligne.domains : [ligne.domain];
    // Sur une ligne, `category` est la catégorie affichée : on ne la garde
    // que si elle avait été choisie à la main.
    const commun = { ...ligne, category: ligne.isCategoryManual ? ligne.category : null };
    if (domaines.length === 1) {
      const e = entreePropre({ ...commun, domain: domaines[0] });
      if (e) entrees.push(e);
      continue;
    }
    const messages = Array.isArray(ligne.messages) ? ligne.messages : [];
    const membres = domaines.map(d => String(d).toLowerCase());
    // Chaque email revient au membre le plus précis : un envoi de
    // notify.proton.me appartient à notify.proton.me, pas aussi à proton.me.
    const proprietaire = m => {
      const expediteur = String((m && m.from) || '').toLowerCase().split('@')[1] || '';
      const candidats = membres.filter(d =>
        expediteur === d || expediteur.endsWith('.' + d) || baseDomain(expediteur) === d);
      return candidats.sort((a, b) => b.length - a.length)[0] || null;
    };
    const emailsDe = d => messages.filter(m => proprietaire(m) === d);
    let reste = Number(ligne.count) || 0;
    const parties = membres.map(d => ({ d, messages: emailsDe(d) }));
    for (const p of parties) if (p.d !== ligne.domain) reste -= p.messages.length;
    for (const p of parties) {
      const dates = p.messages.map(m => m.date).filter(Boolean).sort();
      const e = entreePropre({
        ...commun,
        domain: p.d,
        messages: p.messages,
        count: p.d === ligne.domain ? Math.max(reste, p.messages.length) : p.messages.length,
        firstSeen: dates[0] || (p.d === ligne.domain ? ligne.firstSeen : null),
        lastSeen: dates[dates.length - 1] || (p.d === ligne.domain ? ligne.lastSeen : null),
        // Le lien de désinscription d'une ligne vient d'un seul de ses domaines.
        unsubscribe: p.d === ligne.domain ? ligne.unsubscribe : null,
        unsubscribeOneClick: p.d === ligne.domain ? ligne.unsubscribeOneClick : false,
        unsubscribeMailto: p.d === ligne.domain ? ligne.unsubscribeMailto : null,
        newsletterCount: p.d === ligne.domain ? ligne.newsletterCount : 0,
        newsletterUnread: p.d === ligne.domain ? ligne.newsletterUnread : 0,
        lastNewsletter: p.d === ligne.domain ? ligne.lastNewsletter : null
      });
      if (e) entrees.push(e);
    }
  }
  return entrees;
}

function fusionnerEntree(locale, importee) {
  if (!locale) return importee;
  const recente = String(importee.updatedAt || '') > String(locale.updatedAt || '') ? importee : locale;
  const premiere = [locale.firstSeen, importee.firstSeen].filter(Boolean).sort();
  const derniere = [locale.lastSeen, importee.lastSeen].filter(Boolean).sort();
  return {
    domain: locale.domain,
    serviceName: locale.serviceName || importee.serviceName,
    firstSeen: premiere[0] || null,
    lastSeen: derniere[derniere.length - 1] || null,
    count: Math.max(Number(locale.count) || 0, Number(importee.count) || 0),
    sampleSubject: locale.sampleSubject || importee.sampleSubject,
    newsletter: Boolean(locale.newsletter || importee.newsletter),
    confirmed: locale.confirmed !== false || importee.confirmed !== false,
    unsubscribe: locale.unsubscribe || importee.unsubscribe || null,
    unsubscribeOneClick: locale.unsubscribe ? Boolean(locale.unsubscribeOneClick) : Boolean(importee.unsubscribeOneClick),
    unsubscribeMailto: locale.unsubscribeMailto || importee.unsubscribeMailto || null,
    newsletterCount: Math.max(Number(locale.newsletterCount) || 0, Number(importee.newsletterCount) || 0),
    newsletterUnread: Math.max(Number(locale.newsletterUnread) || 0, Number(importee.newsletterUnread) || 0),
    lastNewsletter: [locale.lastNewsletter, importee.lastNewsletter].filter(Boolean).sort().pop() || null,
    unsubscribedAt: recente.unsubscribedAt || null,
    rgpdRequestedAt: recente.rgpdRequestedAt || null,
    discoveredAt: locale.discoveredAt && importee.discoveredAt
      ? [locale.discoveredAt, importee.discoveredAt].sort()[0] : null,
    lastScanAt: [locale.lastScanAt, importee.lastScanAt].filter(Boolean).sort().pop() || null,
    messages: mergeMessages(locale.messages, importee.messages),
    status: recente.status || 'a_trier',
    notes: recente.notes || '',
    category: recente.category || null,
    updatedAt: recente.updatedAt || locale.updatedAt || null
  };
}

/**
 * Fusionne une sauvegarde dans l'état d'une adresse. Fonction pure : elle
 * reçoit et renvoie { comptes, ignored, groups, categories }, plus un bilan.
 */
function fusionnerSauvegarde(etat, sauvegarde) {
  const comptes = { ...etat.comptes };
  const bilan = { ajoutes: 0, fusionnes: 0, masques: 0, groupes: 0 };

  for (const entree of entreesDeSauvegarde(sauvegarde)) {
    if (comptes[entree.domain]) bilan.fusionnes += 1; else bilan.ajoutes += 1;
    comptes[entree.domain] = fusionnerEntree(comptes[entree.domain], entree);
  }

  const ignored = [...new Set([
    ...etat.ignored,
    ...(Array.isArray(sauvegarde.ignored) ? sauvegarde.ignored : [])
      .map(d => String(d).toLowerCase())
      .filter(d => VALID_DOMAIN.test(d))
  ])].sort();
  for (const domain of Object.keys(comptes)) {
    if (isIgnored(ignored, domain)) { delete comptes[domain]; bilan.masques += 1; }
  }

  let groups = etat.groups;
  for (const g of Array.isArray(sauvegarde.groups) ? sauvegarde.groups : []) {
    const nom = texteOuNull(g && g.name, 200);
    const domaines = (Array.isArray(g && g.domains) ? g.domains : [])
      .map(d => String(d).toLowerCase())
      .filter(d => VALID_DOMAIN.test(d));
    if (!nom || domaines.length < 2) continue;
    groups = ajouterGroupe(groups, nom, domaines);
    bilan.groupes += 1;
  }

  const categories = { ...etat.categories };
  const importees = sauvegarde.categories && typeof sauvegarde.categories === 'object' ? sauvegarde.categories : {};
  for (const [ancien, nouveau] of Object.entries(importees)) {
    const cle = texteOuNull(ancien, 100);
    if (!cle || cle === '__proto__') continue;
    if (nouveau === null || typeof nouveau === 'string') categories[cle] = nouveau ? nouveau.slice(0, 100) : null;
  }

  return { comptes, ignored, groups, categories, bilan };
}

// Une sauvegarde complète peut peser plusieurs Mo : 50 emails par service.
const IMPORT_TAILLE_MAX = '25mb';

app.post('/api/import', express.json({ limit: IMPORT_TAILLE_MAX }), (req, res) => {
  const sauvegarde = req.body || {};
  const email = String(sauvegarde.email || '').trim().toLowerCase();
  if (sauvegarde._outil !== 'Scanyt' || !/^[^@\s]+@[^@\s]+$/.test(email) || email.length > 320) {
    return res.status(400).json({ error: "Ce fichier n'est pas une sauvegarde Scanyt (export JSON)." });
  }

  const resultat = fusionnerSauvegarde({
    comptes: readAll()[email] || {},
    ignored: ignoredFor(email),
    groups: manualGroupsFor(email),
    categories: categoryMapFor(email)
  }, sauvegarde);

  const all = readAll();
  all[email] = resultat.comptes;
  writeAll(all);
  const ignores = readIgnored();
  ignores[email] = resultat.ignored;
  writeIgnored(ignores);
  const groupes = readGroups();
  groupes[email] = resultat.groups;
  writeGroups(groupes);
  const categories = readCategoryMap();
  categories[email] = resultat.categories;
  writeCategoryMap(categories);

  // Les liens « Gérer » corrigés à la main : ceux déjà présents ici priment.
  const liens = sauvegarde.deletionLinks && typeof sauvegarde.deletionLinks === 'object' ? sauvegarde.deletionLinks : {};
  const connus = { ...localKnowledge().deletionLinks };
  let nouveauxLiens = 0;
  for (const [domaine, url] of Object.entries(liens)) {
    const d = String(domaine).toLowerCase();
    if (!VALID_DOMAIN.test(d) || connus[d] || typeof url !== 'string' || !/^https?:\/\//i.test(url)) continue;
    connus[d] = url.slice(0, 2000);
    nouveauxLiens += 1;
  }
  if (nouveauxLiens) writeLocalKnowledge({ deletionLinks: connus });

  const map = categoryMapFor(email);
  const accounts = groupAccounts(Object.values(resultat.comptes), resultat.groups).map(a => decorate(a, map));
  res.json({ email, accounts, ignored: resultat.ignored, bilan: resultat.bilan });
});

// Retire un service de la liste. Il pourra revenir lors d'un prochain scan.
app.delete('/api/accounts', (req, res) => {
  const corps = req.body || {};
  if (!corps.email) return res.status(400).json({ error: 'email requis.' });
  const mail = String(corps.email).toLowerCase();
  const domaines = domainesVises(mail, corps);
  if (!domaines) return res.status(400).json({ error: 'domain ou domains requis.' });
  res.json({ ok: true, removed: deleteAccounts(mail, domaines) });
});

// Regroupements manuels : réunir plusieurs services sous un nom choisi.
app.post('/api/groups', (req, res) => {
  const { email, name, domains } = req.body || {};
  if (!email || !name || !Array.isArray(domains)) {
    return res.status(400).json({ error: 'email, name et domains requis.' });
  }
  const nom = String(name).trim();
  if (!nom) return res.status(400).json({ error: 'Le nom du groupe ne peut pas être vide.' });
  if (domains.length < 2) return res.status(400).json({ error: 'Il faut au moins deux services.' });
  res.json({ groups: saveManualGroup(String(email).toLowerCase(), nom, domains) });
});

app.delete('/api/groups', (req, res) => {
  const { email, name } = req.body || {};
  if (!email || !name) return res.status(400).json({ error: 'email et name requis.' });
  res.json({ groups: deleteManualGroup(String(email).toLowerCase(), String(name)) });
});

// Liste noire : retire le service ET l'empêche de revenir.
app.get('/api/ignored', (req, res) => {
  const email = String(req.query.email || '').trim().toLowerCase();
  if (!email) return res.status(400).json({ error: 'Paramètre email requis.' });
  res.json({ email, ignored: ignoredFor(email) });
});

app.post('/api/ignored', (req, res) => {
  const corps = req.body || {};
  if (!corps.email) return res.status(400).json({ error: 'email requis.' });
  const mail = String(corps.email).toLowerCase();
  const domaines = domainesVises(mail, corps);
  if (!domaines) return res.status(400).json({ error: 'domain ou domains requis.' });
  res.json({ ignored: ignoreDomains(mail, domaines) });
});

app.delete('/api/ignored', (req, res) => {
  const { email, domain } = req.body || {};
  if (!email || !domain) return res.status(400).json({ error: 'email et domain requis.' });
  res.json({ ignored: unignoreDomain(String(email).toLowerCase(), String(domain)) });
});

app.patch('/api/accounts', (req, res) => {
  const { email, domain, domains, status, notes, category, unsubscribed, rgpdRequested } = req.body || {};
  if (!email) return res.status(400).json({ error: 'email requis.' });

  // Action groupée : on répond avec le nombre de services touchés, le front
  // recharge la liste ensuite. Renvoyer vingt lignes décorées n'apporterait
  // rien qu'un rechargement ne fasse mieux.
  if (Array.isArray(domains)) {
    const mail = String(email).toLowerCase();
    const vises = domainesVises(mail, { domains });
    if (!vises) return res.status(400).json({ error: 'Liste de services vide ou trop longue.' });
    return res.json({ updated: updateAccounts(mail, vises, { status, notes, category, unsubscribed, rgpdRequested }) });
  }

  if (!domain) return res.status(400).json({ error: 'domain ou domains requis.' });
  try {
    const mail = String(email).toLowerCase();
    // La ligne représente une marque : le statut, la note et la catégorie
    // s'appliquent à tous ses domaines, sinon ils divergeraient en silence.
    const membres = groupMembers(mail, String(domain));
    let touche = false;
    for (const d of membres) {
      try { updateAccount(mail, d, { status, notes, category, unsubscribed, rgpdRequested }); touche = true; } catch (err) { /* membre absent */ }
    }
    if (!touche) throw new Error('Entrée introuvable');
    const stockees = readAll()[mail] || {};
    const groupe = groupAccounts(membres.map(d => stockees[d]).filter(Boolean), manualGroupsFor(mail))[0];
    res.json(decorate(groupe, categoryMapFor(mail)));
  } catch (err) {
    res.status(404).json({ error: err.message });
  }
});

// Démarre un scan et rend la main immédiatement avec un identifiant de job.
// Le plafond vient de l'interface : on le borne pour qu'une valeur farfelue
// (0, du texte, un million) ne fasse pas un scan vide ou interminable.
const MAX_MESSAGES_LIMITE = 200000;
function plafondMessages(valeur) {
  const n = Number(valeur);
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_MAX_MESSAGES;
  return Math.min(Math.max(Math.round(n), 100), MAX_MESSAGES_LIMITE);
}

app.post('/api/scan', (req, res) => {
  const {
    email, password, accessToken, host, port, secure, mailbox, yearsBack, includeNewsletters, maxMessages,
    allSenders
  } = req.body || {};

  if (!email || !(password || accessToken) || !host || !port) {
    return res.status(400).json({ error: 'email, password (ou accessToken), host et port sont requis.' });
  }

  cleanupJobs();

  const jobId = crypto.randomUUID();
  const job = {
    status: 'running',
    progress: { percent: 0, label: 'Démarrage…' },
    track: makeProgressTracker(),
    lastInfo: null,
    accounts: null,
    error: null,
    email: String(email).toLowerCase(),
    createdAt: Date.now()
  };
  jobs.set(jobId, job);

  // Le scan tourne en arrière-plan ; le mot de passe reste uniquement en mémoire
  // le temps de cet appel et n'est jamais stocké dans le job.
  scanMailbox({
    host: String(host),
    port: Number(port),
    secure: secure !== false,
    user: String(email),
    pass: password ? String(password) : undefined,
    accessToken: accessToken ? String(accessToken) : undefined,
    mailbox: mailbox ? String(mailbox) : '__ALL__',
    yearsBack: yearsBack ? Number(yearsBack) : undefined,
    includeNewsletters: Boolean(includeNewsletters),
    allSenders: Boolean(allSenders),
    maxMessages: plafondMessages(maxMessages),
    // On mémorise seulement l'état brut : la conversion en pourcentage se fait
    // au moment où le front interroge, pour que la barre continue d'avancer
    // pendant les longues phases silencieuses.
    onProgress: info => {
      job.lastInfo = info;
    }
  })
    .then(results => {
      const { comptes, bilan } = mergeScanResults(job.email, results);
      const map = categoryMapFor(job.email);
      job.accounts = groupAccounts(Object.values(comptes), manualGroupsFor(job.email))
        .map(a => decorate(a, map));
      job.scanned = results.length;
      job.bilan = bilan;
      job.progress = { percent: 100, label: 'Terminé' };
      job.status = 'done';
    })
    .catch(err => {
      const isAuthError = err.authenticationFailed || /auth/i.test(err.message || '');
      job.error = isAuthError && accessToken
        ? "Microsoft a refusé l'accès à la boîte. Reconnecte-toi avec Microsoft, puis relance le scan."
        : isAuthError
          ? "Échec de l'authentification IMAP. Vérifie l'adresse, le mot de passe d'application, et que l'accès IMAP est activé sur ton compte."
          : `Erreur pendant le scan : ${err.message || 'inconnue'}`;
      job.status = 'error';
    });

  res.status(202).json({ jobId });
});

// Consulté en boucle par le front pour alimenter la barre de progression.
app.get('/api/scan/:jobId', (req, res) => {
  const job = jobs.get(req.params.jobId);
  if (!job) return res.status(404).json({ error: 'Scan introuvable (ou expiré).' });

  if (job.status === 'running') job.progress = job.track(job.lastInfo);

  const payload = { status: job.status, progress: job.progress };
  if (job.status === 'done') {
    payload.accounts = job.accounts;
    payload.scanned = job.scanned;
    payload.bilan = job.bilan;
    payload.email = job.email;
  }
  if (job.status === 'error') payload.error = job.error;
  res.json(payload);
});

// Le serveur ne s'ouvre que si ce fichier est lancé directement (npm start).
// Ainsi les tests peuvent charger ce module pour éprouver ses fonctions sans
// mettre un port en écoute.
if (require.main === module) {
  app.listen(PORT, '127.0.0.1', () => {
    console.log(`Scanyt est prêt sur http://localhost:${PORT}`);
  });
}

module.exports = {
  app, PORT, DATA_DIR,
  brandKey, brandName, groupAccounts,
  categoryFor, deletionInfoFor, logoCandidates, fuiteFor,
  versCsv, ligneExport,
  fusionnerSauvegarde, mergeScanResults, updateAccount
};
